// Custom Countdown Component
class MyCountDown extends HTMLElement {
  constructor() {
    super();
    this.endDate = null;
    this.interval = null;
    this.observer = null;
  }

  connectedCallback() {
    this.init();
    
    // Watch for attribute changes (for Shopify design mode)
    this.observer = new MutationObserver(() => {
      if (this.interval) {
        clearInterval(this.interval);
      }
      this.init();
    });
    
    this.observer.observe(this, {
      attributes: true,
      attributeFilter: ['data-end-date']
    });
  }

  disconnectedCallback() {
    if (this.interval) {
      clearInterval(this.interval);
    }
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  init() {
    const endDateStr = this.getAttribute('data-end-date');
    if (!endDateStr) return;

    // Parse the date string properly - handle ISO format YYYY-MM-DDTHH:MM:SS
    // If no timezone is specified, treat as local time
    let endDate;
    if (endDateStr.includes('T')) {
      // ISO format without timezone - treat as local time
      // If it doesn't end with Z or +/- timezone, it's local time
      if (!endDateStr.match(/[+-]\d{2}:\d{2}$/) && !endDateStr.endsWith('Z')) {
        // Local time - parse directly
        endDate = new Date(endDateStr);
      } else {
        // Has timezone info - parse as-is
        endDate = new Date(endDateStr);
      }
    } else {
      // Fallback for other formats
      endDate = new Date(endDateStr);
    }
    
    this.endDate = endDate.getTime();
    if (isNaN(this.endDate)) {
      console.error('Invalid date format:', endDateStr);
      return;
    }

    // Show elements
    const elements = this.querySelectorAll('[data-day], [data-hour], [data-min], [data-sec]');
    elements.forEach(el => {
      el.style.opacity = '1';
    });

    this.updateCountdown();
    this.interval = setInterval(() => this.updateCountdown(), 1000);
  }

  updateCountdown() {
    const now = new Date().getTime();
    const distance = this.endDate - now;

    if (distance < 0) {
      this.setValues(0, 0, 0, 0);
      if (this.interval) {
        clearInterval(this.interval);
      }
      return;
    }

    const days = Math.floor(distance / (1000 * 60 * 60 * 24));
    const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((distance % (1000 * 60)) / 1000);

    this.setValues(days, hours, minutes, seconds);
  }

  setValues(days, hours, minutes, seconds) {
    const dayEl = this.querySelector('[data-day]');
    const hourEl = this.querySelector('[data-hour]');
    const minEl = this.querySelector('[data-min]');
    const secEl = this.querySelector('[data-sec]');

    if (dayEl) dayEl.textContent = this.padZero(days);
    if (hourEl) hourEl.textContent = this.padZero(hours);
    if (minEl) minEl.textContent = this.padZero(minutes);
    if (secEl) secEl.textContent = this.padZero(seconds);
  }

  padZero(num) {
    return num < 10 ? '0' + num : num.toString();
  }
}

if (!customElements.get('my-count-down')) {
  customElements.define('my-count-down', MyCountDown);
}

// Typewriter effect with loop (erase and repeat)
class MyTypewriter extends HTMLElement {
  constructor() {
    super();
    this.texts = [];
    this.currentTextIndex = 0;
    this.currentCharIndex = 0;
    this.isTyping = true;
    this.typingSpeed = 50;
    this.eraseSpeed = 30;
    this.pauseAfterTyping = 2000;
    this.pauseAfterErasing = 500;
    this.textNodes = [];
  }

  connectedCallback() {
    const textsStr = this.getAttribute('data-texts');
    if (!textsStr) return;

    // Split by | or newline for multiple texts
    this.texts = textsStr.split('|').map(t => t.trim()).filter(t => t);
    if (this.texts.length === 0) return;

    this.innerHTML = '';
    this.typeCharacter();
  }

  typeCharacter() {
    const currentText = this.texts[this.currentTextIndex];
    
    if (this.currentCharIndex < currentText.length) {
      const char = document.createTextNode(currentText[this.currentCharIndex]);
      this.appendChild(char);
      this.textNodes.push(char);
      this.currentCharIndex++;
      
      setTimeout(() => this.typeCharacter(), this.typingSpeed);
    } else {
      setTimeout(() => this.eraseCharacter(), this.pauseAfterTyping);
    }
  }

  eraseCharacter() {
    if (this.textNodes.length > 0) {
      const lastNode = this.textNodes.pop();
      if (lastNode && lastNode.parentNode) {
        lastNode.parentNode.removeChild(lastNode);
      }
      setTimeout(() => this.eraseCharacter(), this.eraseSpeed);
    } else {
      // Move to next text or loop back
      this.currentTextIndex = (this.currentTextIndex + 1) % this.texts.length;
      this.currentCharIndex = 0;
      setTimeout(() => this.typeCharacter(), this.pauseAfterErasing);
    }
  }
}

if (!customElements.get('my-typewriter')) {
  customElements.define('my-typewriter', MyTypewriter);
}

// Custom Modal Component
class MyModal extends HTMLElement {
  constructor() {
    super();
    this.modalName = this.getAttribute('name');
    this.isOpen = false;
  }

  connectedCallback() {
    // Find close button
    const closeBtn = this.querySelector('[data-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.close());
    }

    // Handle open modal triggers
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-open-modal]');
      if (trigger && trigger.getAttribute('data-open-modal') === this.modalName) {
        e.preventDefault();
        this.open();
      }
    });

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });
  }

  open() {
    this.isOpen = true;
    this.setAttribute('open', '');
    document.body.style.overflow = 'hidden';
  }

  close() {
    this.isOpen = false;
    this.removeAttribute('open');
    document.body.style.overflow = '';
  }
}

if (!customElements.get('my-modal')) {
  customElements.define('my-modal', MyModal);
}

// Password Eye Toggle Component
class MyPasswordEye extends HTMLElement {
  constructor() {
    super();
    this.mode = 'password';
  }

  connectedCallback() {
    this.mode = this.getAttribute('data-mode') || 'password';
    const input = this.closest('div').querySelector('input[type="password"]');
    if (!input) return;

    this.addEventListener('click', () => {
      this.toggle(input);
    });
  }

  toggle(input) {
    if (this.mode === 'password') {
      input.type = 'text';
      this.mode = 'text';
      this.setAttribute('data-mode', 'text');
    } else {
      input.type = 'password';
      this.mode = 'password';
      this.setAttribute('data-mode', 'password');
    }
  }
}

if (!customElements.get('my-password-eye')) {
  customElements.define('my-password-eye', MyPasswordEye);
}

// Section Hero Component (for styling)
class SectionHero extends HTMLElement {
  connectedCallback() {
    // Component is ready
  }
}

if (!customElements.get('section-hero')) {
  customElements.define('section-hero', SectionHero);
}
